import { useEffect, useState, useRef, useCallback } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * Admin-only dialog: two-way conversation with any user.
 * - Loads the existing thread when opened (GET /admin/users/{id}/messages)
 * - Renders every message oldest-first with clear "You"/"User" bubbles
 * - Compose box: if the thread is empty → prompts for a subject + first
 *   message + email opt-in; if already active → just a reply textarea
 * - Every send re-fetches so both sides see the latest.
 */
function timeStamp(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return ""; }
}

export default function AdminMessageUserDialog({ user, open, onOpenChange, onSent }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const bottomRef = useRef(null);

  const loadThread = useCallback(async (uid) => {
    setThreadLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${uid}/messages`);
      setMessages(data.messages || []);
    } catch {
      setMessages([]);
    } finally {
      setThreadLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && user?.id) {
      loadThread(user.id);
    } else if (!open) {
      setSubject("");
      setMessage("");
      setSendEmail(true);
      setBusy(false);
      setMessages([]);
    }
  }, [open, user, loadThread]);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages.length]);

  const handleClose = (next) => {
    if (busy) return;
    onOpenChange?.(next);
  };

  const isReplyMode = messages.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!user?.id) return;
    const m = message.trim();
    // For a fresh thread we insist on a subject so the recipient sees a
    // useful headline in their bell + inbox. For a reply the subject
    // sticks with the seed message.
    const s = isReplyMode
      ? (messages.find((mm) => mm.sender_role === "admin" && mm.subject)?.subject
         || "Follow-up")
      : subject.trim();
    if (!isReplyMode && !s) {
      toast.error("Please add a subject for the first message.");
      return;
    }
    if (!m) {
      toast.error("Please write a message.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/message`, {
        subject: s,
        message: m,
        send_email: sendEmail,
      });
      const emailStatus = data?.email_result?.status;
      if (sendEmail && emailStatus === "error") {
        toast.warning(
          `Sent in-app, but email failed: ${data?.email_result?.error || "unknown error"}`
        );
      } else if (sendEmail && emailStatus === "dev") {
        toast.info(`Sent in-app. Email logged (Resend key not set).`);
      } else if (sendEmail) {
        toast.success(`Sent to ${user.name || user.email} — in-app + email.`);
      } else {
        toast.success(`Sent to ${user.name || user.email} — in-app.`);
      }
      setMessage("");
      // Reload the thread so the newest message shows up right away.
      await loadThread(user.id);
      onSent?.(data);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not send message. Try again in a moment."
      );
    } finally {
      setBusy(false);
    }
  };

  const displayName = user?.name || user?.business_name || user?.email || "this user";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-lg"
        data-testid="admin-message-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            {isReplyMode ? `Conversation with ${displayName}` : `Message ${displayName}`}
          </DialogTitle>
          <DialogDescription>
            {isReplyMode
              ? "Every message lives in-app for both sides. Replies show up in their /inbox."
              : "Starts a conversation in their in-app inbox. Reply flows both ways from here."}
          </DialogDescription>
        </DialogHeader>

        {(threadLoading || messages.length > 0) && (
          <div
            data-testid="admin-msg-thread"
            className="rounded-md border p-3 space-y-2 max-h-56 overflow-y-auto"
            style={{
              borderColor: "rgba(0,0,0,0.08)",
              backgroundColor: "rgba(0,0,0,0.02)",
            }}
          >
            {threadLoading ? (
              <div
                className="text-sm py-4 text-center"
                style={{ color: "var(--ink-soft)" }}
              >
                <Loader2 size={14} className="animate-spin inline mr-2" />
                Loading conversation…
              </div>
            ) : (
              messages.map((m) => {
                const mine = m.sender_role === "admin";
                return (
                  <div
                    key={m.id}
                    data-testid={`admin-msg-history-${m.id}`}
                    data-sender={m.sender_role}
                    className={`flex ${mine ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className="rounded-md px-2.5 py-1.5 max-w-[85%]"
                      style={{
                        backgroundColor: mine
                          ? "rgba(107,47,186,0.10)"
                          : "rgba(201,162,75,0.16)",
                      }}
                    >
                      {m.subject && mine && (
                        <div
                          className="text-[10px] uppercase tracking-widest"
                          style={{ color: "var(--terracotta)" }}
                        >
                          {m.subject}
                        </div>
                      )}
                      <div
                        className="text-sm whitespace-pre-wrap"
                        style={{ color: "var(--ink)" }}
                      >
                        {m.body}
                      </div>
                      <div
                        className="text-[10px] mt-1 text-right"
                        style={{ color: "var(--ink-soft)" }}
                      >
                        {mine ? "You" : (user?.name || "User")} · {timeStamp(m.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          {!isReplyMode && (
            <div className="space-y-1.5">
              <Label htmlFor="admin-msg-subject">Subject</Label>
              <Input
                id="admin-msg-subject"
                data-testid="admin-msg-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. Welcome to AddVendTour!"
                maxLength={200}
                autoFocus
                disabled={busy}
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="admin-msg-body">
              {isReplyMode ? "Reply" : "Message"}
            </Label>
            <Textarea
              id="admin-msg-body"
              data-testid="admin-msg-body"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                isReplyMode
                  ? "Type your reply…"
                  : `Hi ${(user?.name || "").split(" ")[0] || "there"},\n\nJust wanted to reach out about…`
              }
              rows={isReplyMode ? 4 : 6}
              maxLength={5000}
              disabled={busy}
              autoFocus={isReplyMode}
            />
            <div
              className="text-[11px] text-right"
              style={{ color: "var(--ink-soft)" }}
            >
              {message.length} / 5000
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="admin-msg-send-email"
              data-testid="admin-msg-send-email"
              checked={sendEmail}
              onCheckedChange={(v) => setSendEmail(Boolean(v))}
              disabled={busy}
            />
            <Label
              htmlFor="admin-msg-send-email"
              className="text-sm font-normal cursor-pointer"
            >
              Also send by email
              {user?.email ? (
                <span
                  className="ml-1"
                  style={{ color: "var(--ink-soft)" }}
                >
                  ({user.email})
                </span>
              ) : null}
            </Label>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
              disabled={busy}
              data-testid="admin-msg-cancel"
            >
              Close
            </Button>
            <Button
              type="submit"
              disabled={busy}
              data-testid="admin-msg-send"
              style={{
                backgroundColor: "var(--terracotta)",
                color: "#fff",
              }}
            >
              {busy ? (
                <>
                  <Loader2 size={14} className="animate-spin mr-2" />
                  Sending…
                </>
              ) : (
                <>
                  <Send size={14} className="mr-2" />
                  {isReplyMode ? "Send reply" : "Send message"}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
